import { execute } from '@/graphql/execute';
import { BillingInterval, SubscriptionPlan, SubscriptionPricingQuery } from '@/graphql/graphql';
import { subscriptionPricingQuery } from '@/queries/subscription.query';
import { useQuery } from '@tanstack/react-query';

export type PlanPrice = SubscriptionPricingQuery['subscriptionPricing']['plans'][0];

export const usePricing = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriptionPricing'],
    queryFn: () => execute(subscriptionPricingQuery),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  const pricing = data?.subscriptionPricing;

  /**
   * Get the flat price for a plan and billing interval
   */
  const getPlanPrice = (plan: SubscriptionPlan, interval: BillingInterval): number => {
    if (!pricing) return 0;
    const planPrice = pricing.plans.find((p) => p.plan === plan);
    if (!planPrice) return 0;
    return interval === 'MONTHLY' ? planPrice.monthlyAmount : planPrice.annualAmount;
  };

  /**
   * Format price in cents to display string
   */
  const formatPrice = (amountCents: number, showCents = true): string => {
    const amount = amountCents / 100;
    if (showCents) {
      return `€${amount.toFixed(0)}`;
    }
    return `€${Math.round(amount)}`;
  };

  return {
    pricing,
    isLoading,
    error,
    getPlanPrice,
    formatPrice,
    currency: pricing?.currency ?? 'eur',
  };
};
